"use client";

import * as Sentry from "@sentry/nextjs";
import { useInitialContext } from "@repo/sdk";
import { CONTEXT_CHANGE_EVENT } from "@repo/sdk/provider";
import {
  getUserTokensCookie,
  setUserTokensCookie,
} from "@repo/session/user-tokens-cookie";
import {
  TOKEN_KEYS,
  USER_TOKENS_COOKIE_SET_EVENT,
} from "@repo/session/user-tokens-observer";
import { useTokenRecovery } from "@repo/session/use-token-recovery";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useSyncExternalStore } from "react";
import BlueberryVisitError from "src/app/intake/[patientId]/owlet/blueberry-visit-error";
import ErrorDisplay from "src/components/ErrorDisplay";
import KinsaHeader from "src/components/KinsaHeader";
import { HeadlineText } from "src/components/KinsaTypography";
import { HomeLoadingSkeleton } from "src/components/skeletons";
import { Routes } from "src/lib/routes";

/**
 * `window.portalInitialContext` is injected by the native shell / simulator —
 * it does not exist during SSR and may be absent for a short window after the
 * client bundle runs (before injection or before `portalInitialContextChanged`).
 * Same subscription model as `InitialContextProvider` in `@repo/sdk`.
 */
function readPortalInitialContextFromWindow(): Record<string, unknown> | null {
  if (typeof window === "undefined") return null;
  const raw = (window as unknown as { portalInitialContext?: unknown })
    .portalInitialContext;
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  return raw as Record<string, unknown>;
}

function subscribePortalInitialContext(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }
  window.addEventListener(CONTEXT_CHANGE_EVENT, onStoreChange);
  const pollingTimer = setInterval(onStoreChange, 10_000);
  return () => {
    window.removeEventListener(CONTEXT_CHANGE_EVENT, onStoreChange);
    clearInterval(pollingTimer);
  };
}

/** Only allow relative paths to prevent open redirects. */
function sanitizeNextRoute(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) {
    return Routes.HOME;
  }
  return raw;
}

function ContextDebugPanel({
  initialContext,
  cookie,
  missingKeys,
  nextRoute,
}: {
  initialContext: unknown;
  cookie: Record<string, unknown> | null;
  missingKeys: readonly string[];
  nextRoute: string;
}) {
  return (
    <pre className="max-h-64 overflow-auto rounded border border-slate-300 bg-slate-50 p-3 text-left text-xs font-mono text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
      {JSON.stringify(
        {
          nextRoute,
          missingKeys,
          cookie,
          initialContext,
        },
        null,
        2
      )}
    </pre>
  );
}

/**
 * Cold-start landing page: middleware redirects here when the FELT_USER_TOKENS
 * cookie is missing. The UserTokensObserver (mounted in CommonProviders) sets
 * the cookie and dispatches USER_TOKENS_COOKIE_SET_EVENT. This page listens for
 * that event and redirects back to the original route.
 */
export default function TokenSyncClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const nextRoute = sanitizeNextRoute(searchParams.get("next-route"));
  const [ready, setReady] = useState(false);
  const [didTimeout, setDidTimeout] = useState(false);
  // True only when the observer writes a *different* idToken than the one we
  // landed with — never on the rejected-but-well-formed cookie middleware
  // bounced us here for, which would loop. See useTokenRecovery.
  const hasRecovered = useTokenRecovery();

  const initialContext = useInitialContext<Record<string, unknown>>();
  const portalInitialContext = useSyncExternalStore(
    subscribePortalInitialContext,
    readPortalInitialContextFromWindow,
    () => null
  );

  const finalInitialContext = {
    ...(initialContext ?? {}),
    ...(portalInitialContext ?? {}),
  };

  const cookie = getUserTokensCookie();

  const missingKeys =
    cookie == null
      ? TOKEN_KEYS
      : TOKEN_KEYS.filter(
          (key) => typeof cookie[key] !== "string" || cookie[key] === ""
        );

  const contextCustom =
    (finalInitialContext as { custom?: Record<string, unknown> } | null)
      ?.custom ?? {};
  const contextMissingTokenKeys = TOKEN_KEYS.filter(
    (key) => typeof contextCustom[key] !== "string" || contextCustom[key] === ""
  );
  const isProductionEnv = process.env.NEXT_PUBLIC_VERCEL_ENV === "production";
  const showDebugPanel = !isProductionEnv && missingKeys.length > 0;
  const showBypass =
    !isProductionEnv &&
    missingKeys.length > 0 &&
    contextMissingTokenKeys.length > 0;

  console.debug("[token-sync] render", {
    nextRoute,
    ready,
    didTimeout,
    missingKeys,
    contextMissingTokenKeys,
    cookie,
    initialContext,
  });

  useEffect(() => {
    // The observer writes a partial cookie (externalAccountId only) before
    // tokens arrive from the SDK; only mark ready once the brand-required
    // token keys are present, otherwise we'd redirect → middleware bounces
    // back here → loop.
    let intervalId: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const checkReady = (source: "init" | "event" | "poll") => {
      const ok = hasRecovered();
      console.debug("[token-sync] checkReady", {
        source,
        ok,
        cookie: getUserTokensCookie(),
      });
      if (ok) {
        setReady(true);
      }
    };

    const onCookieSet = () => checkReady("event");
    const onPoll = () => {
      if (!ready) checkReady("poll");
    };

    checkReady("init");
    window.addEventListener(USER_TOKENS_COOKIE_SET_EVENT, onCookieSet);

    // Add manual retry: poll every second until ready
    intervalId = setInterval(onPoll, 1000);

    // If we can't satisfy the required tokens, stop spinning forever and show
    // a helpful error state (mirrors Owlet's full-screen error treatment).
    timeoutId = setTimeout(() => {
      console.debug("[token-sync] timeout", {
        nextRoute,
        cookie: getUserTokensCookie(),
        initialContext,
      });
      setDidTimeout(true);
    }, 12_000);

    return () => {
      window.removeEventListener(USER_TOKENS_COOKIE_SET_EVENT, onCookieSet);
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    console.debug("[token-sync] redirect", {
      nextRoute,
      cookie: getUserTokensCookie(),
    });
    // Hard navigation, not `router.replace`. This is a middleware auth-gate
    // crossing: middleware bounced us here because the session-token cookie was
    // missing, and we've just written it client-side. A soft (RSC) navigation
    // reuses the router cache's prefetched `nextRoute → /token-sync` redirect and
    // never sends the fresh cookie, so middleware bounces us straight back —
    // an infinite /token-sync ⇄ nextRoute loop (surfaced by Next 16.3's prefetch
    // caching). A full document load re-runs middleware from scratch with the
    // cookie present, which resolves cleanly.
    window.location.assign(nextRoute);
  }, [ready, nextRoute]);

  useEffect(() => {
    if (!didTimeout) return;
    const currentCookie = getUserTokensCookie();
    const currentMissingKeys =
      currentCookie == null
        ? [...TOKEN_KEYS]
        : TOKEN_KEYS.filter(
            (key) =>
              typeof currentCookie[key] !== "string" ||
              currentCookie[key] === ""
          );
    Sentry.captureMessage("Token sync timed out: missing required keys", {
      level: "warning",
      extra: {
        nextRoute,
        missingKeys: currentMissingKeys,
        contextMissingTokenKeys,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didTimeout]);

  const handleBypass = () => {
    const externalAccountId =
      (initialContext as { externalAccountId?: string } | null)
        ?.externalAccountId ?? "BYPASS";
    const payload: Record<string, unknown> = {
      externalAccountId,
      idToken: "BYPASS_ID_TOKEN",
      refreshToken: "BYPASS_REFRESH_TOKEN",
    };
    console.debug("[token-sync] bypass", payload);
    setUserTokensCookie(payload);
    setReady(true);
  };

  if (!ready && didTimeout) {
    const detail = `nextRoute=${nextRoute} missingKeys=${missingKeys.join(",") || "none"}`;

    if (process.env.NEXT_PUBLIC_BRAND === "owlet") {
      return (
        <BlueberryVisitError
          reason="We couldn't sync your session tokens to continue."
          detail={detail}
        />
      );
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-slate-100 dark:bg-slate-900">
        <div className="mb-8">
          <KinsaHeader size="lg" />
        </div>
        <div className="w-full max-w-lg text-center shadow-xl rounded-lg p-8 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <HeadlineText
            as="h1"
            className="mb-6 text-3xl font-semibold text-red-600 dark:text-red-400"
          >
            Session Sync Failed
          </HeadlineText>
          <ErrorDisplay
            message="We couldn’t sync your session information. Please close the app and try again."
            nextRoute={nextRoute}
          />
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400 break-words font-mono">
            {detail}
          </p>
          <div className="mt-4">
            <ContextDebugPanel
              initialContext={initialContext}
              cookie={cookie}
              missingKeys={missingKeys}
              nextRoute={nextRoute}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="fixed inset-x-0 top-0 z-50 p-3">
        {showDebugPanel && (
          <ContextDebugPanel
            initialContext={initialContext}
            cookie={cookie}
            missingKeys={missingKeys}
            nextRoute={nextRoute}
          />
        )}
        {showBypass && (
          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={handleBypass}
              className="rounded-md border-2 border-red-600 bg-yellow-300 px-6 py-3 text-base font-bold uppercase tracking-wide text-red-800 shadow-lg hover:bg-yellow-200"
            >
              BYPASS (dev only)
            </button>
          </div>
        )}
      </div>
      <HomeLoadingSkeleton />
    </div>
  );
}
